/**
 * Browser-side reading of an audio file: decoding it to samples and checking that it is audio at all. Uses
 * Web Audio and an <audio> element, so it runs only in a browser (the pure checks it relies on are in
 * containers.ts and run anywhere).
 */
import { fileSampleRate, sniffFileKind } from "./containers";

export interface DecodedAudio {
  channels: Float32Array[];
  sampleRate: number;
  duration: number;
  /** The sample rate the file itself states, when it could be read (the decoder's rate is `sampleRate`). */
  fileRate: number | null;
}

/**
 * Decodes a file to samples. With `matchFileRate` the decoder is asked to run at the file's own sample rate (read
 * from its header), so a 44.1 kHz file stays 44.1 kHz instead of being resampled to the device's 48 kHz on the way
 * in; when the file states no rate, or the browser refuses that rate, the device rate is used and `sampleRate`
 * says so. Throws when the browser cannot decode the file.
 */
export async function decodeAudioFile(file: Blob, opts: { matchFileRate?: boolean; maxChannels?: number } = {}): Promise<DecodedAudio> {
  const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const fileRate = opts.matchFileRate ? await fileSampleRate(file) : null;
  const bytes = await file.arrayBuffer();
  let ctx: AudioContext | null = null;
  if (fileRate) { try { ctx = new Ctx({ sampleRate: fileRate }); } catch { ctx = null; } }
  if (!ctx) ctx = new Ctx();
  try {
    const buf = await ctx.decodeAudioData(bytes);
    const n = Math.min(opts.maxChannels ?? buf.numberOfChannels, buf.numberOfChannels);
    return { channels: Array.from({ length: n }, (_, c) => buf.getChannelData(c)), sampleRate: buf.sampleRate, duration: buf.duration, fileRate };
  } finally {
    ctx.close().catch(() => {});
  }
}

export type AudioProbe =
  /** `playable` is false when the browser cannot play it but the file has a real audio signature (WMA, AMR ...), which ffmpeg may still convert. */
  | { readable: true; duration: number | null; playable: boolean }
  | { readable: false };

/**
 * The decision behind probeAudioFile, kept pure so it can be tested: a file the browser plays is audio; one it
 * cannot play is still accepted when its first bytes are a known audio signature (a converter should not turn away
 * a WMA the browser cannot play), and is rejected when they are not (text renamed .mp3, an image, an empty file).
 */
export function judgeAudioProbe(o: { browser: "ok" | "error" | "timeout"; duration: number | null; kind: string | null }): AudioProbe {
  if (o.browser !== "error") return { readable: true, duration: o.duration, playable: o.browser === "ok" };
  return o.kind ? { readable: true, duration: null, playable: false } : { readable: false };
}

/**
 * The same check the Audio Trimmer makes (does the browser's own decoder accept it), plus a look at the file's first
 * bytes for formats the browser cannot play. Choosing a file spends nothing, so a file that fails here says so and
 * the tool stays off. A file that is slow to open (8 s) is let through with its length unknown, not rejected.
 */
export async function probeAudioFile(file: Blob): Promise<AudioProbe> {
  const browser = await new Promise<{ state: "ok" | "error" | "timeout"; duration: number | null }>((resolve) => {
    const a = document.createElement("audio");
    const url = URL.createObjectURL(file);
    const done = (state: "ok" | "error" | "timeout", duration: number | null) => { URL.revokeObjectURL(url); resolve({ state, duration }); };
    a.preload = "metadata";
    a.onloadedmetadata = () => done("ok", Number.isFinite(a.duration) && a.duration > 0 ? a.duration : null);
    a.onerror = () => done("error", null);
    setTimeout(() => done("timeout", null), 8000);
    a.src = url;
  });
  const kind = browser.state === "error" ? await sniffFileKind(file) : null;
  return judgeAudioProbe({ browser: browser.state, duration: browser.duration, kind });
}
